import * as yup from 'yup';
import { errorMessages } from '~/constants/errorMessages.ts';
import { MAX_INPUT_LENGTH } from './constants';
import { regularExpressions } from '~/constants/regularExpressions';
import * as Yup from 'yup';
import { RNOKPPIdentNumberSchema } from '../../validationSchemas';

export const SelectItemYup = yup.object({
  value: yup.string().required(errorMessages.FIELD_REQUIRED),
  label: yup.string().required(errorMessages.FIELD_REQUIRED),
});

const endDateSchema = yup
  .string()
  .notRequired()
  .nullable()
  .test('end-after-start', errorMessages.PEP_END_AFTER_START_DATE, (date, context) => {
    if (!date || !context.parent?.publicFunctionsStartDate) return true;

    const [endDay, endMonth, endYear] = date.split('.');
    const [startDay, startMonth, startYear] = context.parent?.publicFunctionsStartDate.split('.');

    const publicFunctionsEndDate = new Date(`${endYear}-${endMonth}-${endDay}`);
    const publicFunctionsStartDate = new Date(`${startYear}-${startMonth}-${startDay}`);
    return publicFunctionsEndDate >= publicFunctionsStartDate;
  });

export const relatedPersonPep = yup.object().shape({
  fullName: yup
    .string()
    .required(errorMessages.FIELD_REQUIRED)
    .test('ukr-or-latin', errorMessages.LATIN_AND_UKR_CHARACTERS_ONLY, function (value) {
      if (!value) return true;

      const isUkrainian = regularExpressions.UKR_CHARACTERS_ONLY.test(value);
      const isLatin = regularExpressions.LATIN_CHARACTERS_ONLY.test(value);

      if (isUkrainian || isLatin) return true;

      return this.createError({
        path: this.path,
        message: errorMessages.LATIN_AND_UKR_CHARACTERS_ONLY,
      });
    })
    .max(MAX_INPUT_LENGTH, errorMessages.MAX_LENGTH(MAX_INPUT_LENGTH)),
  identificationNumber: RNOKPPIdentNumberSchema(),
  foreignTaxNumber: yup.string().nullable().notRequired(),
  citizenshipCountry: SelectItemYup.required(errorMessages.FIELD_REQUIRED),
  residenceCountry: SelectItemYup.required(errorMessages.FIELD_REQUIRED),
  pepPosition: yup
    .string()
    .required(errorMessages.FIELD_REQUIRED)
    .max(256, errorMessages.MAX_LENGTH(256)),
  publicFunctionsStartDate: yup.string().required(errorMessages.FIELD_REQUIRED),
  publicFunctionsEndDate: endDateSchema,
});

const clientPep = (schema: yup.ObjectSchema<{}, yup.AnyObject, {}, ''>) =>
  schema.shape({
    fullName: yup
      .string()
      .required(errorMessages.FIELD_REQUIRED)
      .test('ukr-or-latin', errorMessages.LATIN_AND_UKR_CHARACTERS_ONLY, function (value) {
        if (!value) return true;

        const isUkrainian = regularExpressions.UKR_CHARACTERS_ONLY.test(value);
        const isLatin = regularExpressions.LATIN_CHARACTERS_ONLY.test(value);

        if (isUkrainian || isLatin) return true;

        return this.createError({
          path: this.path,
          message: errorMessages.LATIN_AND_UKR_CHARACTERS_ONLY,
        });
      })
      .max(MAX_INPUT_LENGTH, errorMessages.MAX_LENGTH(MAX_INPUT_LENGTH)),
    pepPosition: yup
      .string()
      .required(errorMessages.FIELD_REQUIRED)
      .max(256, errorMessages.MAX_LENGTH(256)),
    publicFunctionsStartDate: yup.string().required(errorMessages.FIELD_REQUIRED),
    publicFunctionsEndDate: endDateSchema,
  });

export const jurisdictionsSchema = Yup.array().of(
  Yup.object().shape({
    country: Yup.object()
      .shape({
        value: Yup.string().nullable(),
        label: Yup.string().required(errorMessages.FIELD_REQUIRED),
      })
      .nullable()
      .required(errorMessages.FIELD_REQUIRED),
    identifyCode: yup.string().nullable().notRequired(),
    reason: Yup.mixed().when('identifyCode', {
      is: (value: string) => !!value,
      then: () => Yup.mixed().nullable().notRequired(),
      otherwise: () => Yup.string().required(errorMessages.ANSWER_OPTION),
    }),
    explanation: Yup.mixed().when('reason', {
      is: (type: string) => type === 'B' || type === 'C',
      then: () => Yup.string().required(errorMessages.FIELD_REQUIRED),
      otherwise: () => Yup.mixed().nullable().notRequired(),
    }),
  })
);

export const pepValidationSchema = (isCRSIFopEnabled: boolean, isSoleTrader: boolean) =>
  yup.array().of(
    yup
      .object()
      .shape({
        isPepsAbsent: yup.boolean().nullable(),
        isPep: yup.boolean().nullable(),
        isFamilyPep: yup.boolean().nullable(),
        isPepRelatedPerson: yup.boolean().nullable(),
        pepInfo: yup
          .object()
          .when('isPep', {
            is: true,
            then: (schema) => clientPep(schema).nullable(),
            otherwise: (schema) => schema.notRequired().nullable(),
          })
          .nullable(),

        familyPepsInfo: yup
          .array()
          .nullable()
          .when('isFamilyPep', {
            is: true,
            then: (schema) => schema.of(relatedPersonPep),
            otherwise: (schema) => schema.notRequired().nullable(),
          })
          .nullable(),

        pepRelatedPersonsInfo: yup
          .array()
          .nullable()
          .when('isPepRelatedPerson', {
            is: true,
            then: (schema) => schema.of(relatedPersonPep),
            otherwise: (schema) => schema.notRequired().nullable(),
          })
          .nullable(),

        hasForeignTaxResidency:
          isCRSIFopEnabled && isSoleTrader
            ? Yup.boolean().required(errorMessages.YES_NO_REQUIRED)
            : Yup.boolean().notRequired().nullable(),
        appeals: Yup.mixed().when('hasForeignTaxResidency', {
          is: true,
          then: () =>
            Yup.object()
              .shape({
                value: Yup.string().nullable(),
                label: Yup.string().required(errorMessages.FIELD_REQUIRED),
              })
              .nullable()
              .required(errorMessages.FIELD_REQUIRED),
          otherwise: () => Yup.mixed().nullable().notRequired(),
        }),
        jurisdictions: Yup.mixed().when('hasForeignTaxResidency', {
          is: true,
          then: () => jurisdictionsSchema,
          otherwise: () => Yup.mixed().nullable().notRequired(),
        }),
        accounts: Yup.mixed().nullable().notRequired(),
      })
      .test('at-least-one-selected', errorMessages.AT_LEAST_ONE_SELECTED, function (values) {
        const { isPep, isPepRelatedPerson, isPepsAbsent, isFamilyPep } = values;
        const filled = [isPep, isPepRelatedPerson, isPepsAbsent, isFamilyPep].filter(Boolean);
        if (filled.length) return true;
        return this.createError({
          path: `${this.path}.isPepsAbsent`,
          message: errorMessages.AT_LEAST_ONE_SELECTED,
        });
      })
  );
